/* Billing: turning an agreed payment structure into a schedule you can invoice
   against. Two ways in — read it off the signed contract, or build it from the
   fee stages already configured on the project. Pure logic, no React. */

import { computeEstimate, defaultConfig, type RateCard } from "./rates";
import type { PaymentSchedule, PaymentMilestone, Project } from "./types";

let seq = 0;
const uid = (p = "") => `${p}${Date.now().toString(36)}${(seq++).toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

/* ---------------------------------------------------------------- from AI */

export interface ReadResult {
  ok: boolean;
  reason?: string;
  contractSum?: number | null;
  currency?: string | null;
  feeBasis?: "lump-sum" | "percentage-of-construction" | null;
  constructionBudget?: number | null;
  feePct?: number | null;
  standardPct?: number | null;
  servicePeriodMonths?: number | null;
  exclusions?: string[];
  disciplines?: { name: string; pct?: number | null; amount?: number | null }[];
  stages?: { label: string; pct?: number | null; amount?: number | null }[];
  deferred?: { pct: number; trigger: string; note?: string | null } | null;
  additionalServices?: { label: string; rate?: number | null; rateMax?: number | null; unit?: string | null; included?: number | null; note?: string | null }[];
  retentionPct?: number | null;
  paymentTermsDays?: number | null;
  milestones?: {
    label: string; basis?: string | null; pct?: number | null; pctBasis?: "fee" | "construction" | null;
    amount?: number | null; stage?: string | null; recurring?: string | null; dueOn?: string | null;
  }[];
}

/** Ask the worker to read a contract (pasted text and/or an uploaded file). */
export async function readPaymentStructure(input: {
  text?: string; fileData?: string; projectName?: string; currency?: string; contractSum?: number;
}): Promise<ReadResult> {
  try {
    const r = await fetch("/api/read-payment-structure", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(input),
    });
    return await r.json() as ReadResult;
  } catch {
    return { ok: false, reason: "unreachable" };
  }
}

/** Fold what the AI found into a schedule. Percentages win where given; a
    milestone with neither a percentage nor an amount is left at zero for the
    studio to fill rather than guessed at. */
export function scheduleFromRead(read: ReadResult, fallback: { contractSum: number; currency: string }, sourceNote?: string): PaymentSchedule {
  const num = (x: unknown) => (typeof x === "number" && isFinite(x) && x > 0 ? x : undefined);
  const constructionBudget = num(read.constructionBudget);
  const feePct = num(read.feePct);
  // contractSum is what WE invoice: the stated fee, else derived from the build cost
  const contractSum =
    num(read.contractSum) ??
    (constructionBudget && feePct ? Math.round(constructionBudget * (feePct / 100)) : undefined) ??
    fallback.contractSum;
  const currency = read.currency || fallback.currency;

  const milestones: PaymentMilestone[] = (read.milestones ?? []).map((m, i) => {
    const pct = num(m.pct) ? m.pct! / 100 : undefined;
    // a printed figure always wins; otherwise the percentage is applied to the
    // base it was actually quoted against — fee vs construction cost
    const basisIsConstruction = m.pctBasis === "construction";
    const base = basisIsConstruction ? (constructionBudget ?? contractSum) : contractSum;
    const amount = num(m.amount) ?? (pct ? Math.round(base * pct) : 0);
    const rec = m.recurring?.trim();
    return {
      id: uid("pm-"), ref: `PM-${String(i + 1).padStart(2, "0")}`,
      label: m.label?.trim() || `Payment ${i + 1}`,
      basis: m.basis?.trim() || undefined,
      pct, pctBasis: m.pctBasis ?? (pct ? "fee" : undefined),
      amount,
      stage: m.stage?.trim() || undefined,
      recurring: rec ? { every: /month/i.test(rec) ? "month" : "stage", note: rec } : undefined,
      dueOn: m.dueOn || undefined,
      status: "scheduled" as const,
    };
  });

  const disciplines = (read.disciplines ?? [])
    .map((d) => ({
      name: d.name?.trim() || "—",
      pct: num(d.pct) ? d.pct! / 100 : undefined,
      amount: num(d.amount) ?? (num(d.pct) && constructionBudget ? Math.round(constructionBudget * (d.pct! / 100)) : 0),
    }))
    .filter((d) => d.name !== "—");

  const stages = (read.stages ?? []).map((s) => ({
    label: s.label?.trim() || "Stage",
    pct: num(s.pct) ? s.pct! / 100 : undefined,
    amount: num(s.amount) ?? (num(s.pct) ? Math.round(contractSum * (s.pct! / 100)) : 0),
  }));

  return {
    currency, contractSum,
    feeBasis: read.feeBasis ?? (constructionBudget && feePct ? "percentage-of-construction" : undefined),
    constructionBudget, feePct: feePct ? feePct / 100 : undefined,
    standardPct: num(read.standardPct) ? read.standardPct! / 100 : undefined,
    servicePeriodMonths: num(read.servicePeriodMonths),
    additionalServices: servicesFromRead(read).length ? servicesFromRead(read) : undefined,
    exclusions: (read.exclusions ?? []).filter(Boolean),
    disciplines: disciplines.length ? disciplines : undefined,
    stages: stages.length ? stages : undefined,
    deferred: read.deferred && num(read.deferred.pct)
      ? { pct: read.deferred.pct / 100, trigger: read.deferred.trigger?.trim() || "", note: read.deferred.note?.trim() || undefined }
      : undefined,
    retentionPct: typeof read.retentionPct === "number" ? read.retentionPct : undefined,
    paymentTermsDays: typeof read.paymentTermsDays === "number" ? read.paymentTermsDays : undefined,
    source: "contract", sourceNote,
    milestones, createdOn: new Date().toISOString(),
  };
}

/** The extras the engagement charges for, mapped onto the project's rate card. */
export function servicesFromRead(read: ReadResult): { label: string; rate: number; rateMax?: number; unit?: string; included?: number; note?: string }[] {
  return (read.additionalServices ?? [])
    .filter((s) => s.label?.trim())
    .map((s) => ({
      label: s.label.trim(),
      rate: typeof s.rate === "number" ? s.rate : 0,
      rateMax: typeof s.rateMax === "number" ? s.rateMax : undefined,
      unit: s.unit?.trim() || undefined,
      included: typeof s.included === "number" ? s.included : undefined,
      note: s.note?.trim() || undefined,
    }));
}

/* ------------------------------------------------------------ from stages */

/** Build the schedule from the project's own fee stages — the fallback when
    there's no contract to read. Amounts follow the configured stage split of
    the estimated fee, so the schedule always totals the fee. */
export function scheduleFromStages(p: Project, rateCard: RateCard): PaymentSchedule {
  const cfg = p.estimate ?? defaultConfig(p.budget, p.currency);
  const est = computeEstimate(cfg, rateCard);
  const currency = p.currency || "USD";
  const budget = cfg.budget || p.budget || 0;
  // the stage amounts come from the fee's own phase split, so a fixed
  // construction fee and the rounding both land where the estimate put them
  const milestones: PaymentMilestone[] = est.phases.map((ph, i) => ({
    id: uid("pm-"), ref: `PM-${String(i + 1).padStart(2, "0")}`,
    label: ph.label,
    basis: ph.construction ? "Paid across the construction period" : `On completion of ${ph.label.toLowerCase()}`,
    pct: ph.pct,
    amount: ph.amount,
    status: "scheduled" as const,
  }));
  return {
    currency, contractSum: Math.round(est.total),
    feeBasis: budget > 0 ? "percentage-of-construction" : undefined,
    constructionBudget: budget > 0 ? budget : undefined,
    feePct: budget > 0 ? est.total / budget : undefined,
    fullFee: est.discount > 0 ? est.gross : undefined,
    source: "stages", sourceNote: "Built from the project's fee stages",
    paymentTermsDays: 14,
    milestones, createdOn: new Date().toISOString(),
  };
}

/** A schedule built from the fee stages is a derived number: when the budget
 *  or the scope moves, so must it. True when the schedule on record no longer
 *  matches what the stages would produce today. */
export function scheduleIsStale(p: Project, rateCard: RateCard): boolean {
  const b = p.billing;
  if (!b || b.source !== "stages") return false;
  const est = computeEstimate(p.estimate ?? defaultConfig(p.budget, p.currency), rateCard);
  return Math.round(est.total) !== Math.round(b.contractSum);
}

/** True when nothing has been raised against the schedule yet, so it can be
 *  rebuilt in place without touching an invoice. */
export function scheduleUntouched(b: PaymentSchedule | undefined): boolean {
  if (!b) return false;
  if (b.billingMode === "valuation") return false;
  if (b.retainer?.invoiceId) return false;
  return (b.milestones ?? []).every((m) => m.status === "scheduled" && !m.invoiceId);
}

/** The stage-built schedule rebuilt from the current fee, keeping everything
 *  the studio set by hand on it — terms, tax, retainer, pay link, issue date,
 *  milestone ids and due dates. Returns null when nothing would change. */
export function refreshStageSchedule(p: Project, rateCard: RateCard): PaymentSchedule | null {
  const b = p.billing;
  if (!b || b.source !== "stages") return null;
  if (!scheduleIsStale(p, rateCard)) return null;
  const fresh = scheduleFromStages(p, rateCard);
  const milestones = fresh.milestones.map((m) => {
    const prior = (b.milestones ?? []).find((x) => x.label.trim().toLowerCase() === m.label.trim().toLowerCase() || x.ref === m.ref);
    return prior ? { ...m, id: prior.id, ref: prior.ref, dueOn: prior.dueOn, status: prior.status, invoiceId: prior.invoiceId, items: prior.items } : m;
  });
  return {
    ...b,
    contractSum: fresh.contractSum, currency: fresh.currency,
    feeBasis: fresh.feeBasis, constructionBudget: fresh.constructionBudget, feePct: fresh.feePct, fullFee: fresh.fullFee,
    sourceNote: "Built from the project's fee stages",
    milestones,
    retainer: b.retainer ? { ...b.retainer, amount: Math.round(fresh.contractSum * (b.retainer.pct / 100)) } : undefined,
  };
}

/* --------------------------------------------------------------- readouts */

/** What the client was given off the fee: the full amount, the saving and the
 *  percentage. Read from the stated full fee where there is one, else from the
 *  standard rate quoted against the agreed one. Null when there is no discount. */
export function feeDiscount(b: { contractSum: number; fullFee?: number; standardPct?: number; feePct?: number; constructionBudget?: number } | undefined | null): { full: number; saving: number; pct: number; standardPct?: number; agreedPct?: number } | null {
  if (!b || !(b.contractSum > 0)) return null;
  const fromRate = b.standardPct && b.constructionBudget ? Math.round(b.constructionBudget * b.standardPct) : 0;
  const full = b.fullFee && b.fullFee > 0 ? b.fullFee : fromRate;
  if (!(full > b.contractSum)) return null;
  const saving = full - b.contractSum;
  return { full, saving, pct: (saving / full) * 100, standardPct: b.standardPct, agreedPct: b.feePct };
}

export interface BillingTotals {

  /** Everything that will ever be billed on this engagement. */
  billable: number;
  /** Billed so far (milestones invoiced, whether or not they're settled). */
  invoiced: number;
  /** Cash actually received — counts part payments, not just settled invoices. */
  paid: number;
  /** Owed right now on invoices already issued. */
  dueNow: number;
  /** Contract value not yet invoiced — future work, not money owed. */
  remainingToBill: number;
  /** Not yet invoiced at all. */
  notYetBilled: number;
  /** Money deferred by a special clause, for information. */
  deferred: number;
  /** VAT/tax — a pass-through, kept separate from the fee figures above. */
  taxInvoiced: number; taxPaid: number; taxDue: number;
  /** The gross balance actually owed now (net fee due + unpaid VAT). */
  dueGross: number;
  scheduled: number; outstanding: number;
  pctInvoiced: number; pctPaid: number; allocated: number; unallocated: number;
}

/** What's been billed against the structure, what's actually landed, and what's
    still to come. Payments are summed from the receipts on each invoice so a
    part payment is reflected honestly rather than counting as nothing. */
/** A payment counts only once the studio has confirmed it landed. A client
 *  saying they paid is evidence to check, not money in the account. */
export function isConfirmed(pay: { status?: string }): boolean {
  return pay.status !== "claimed";
}
/** Money actually received against an invoice, capped at what was billed. */
export function confirmedPaid(inv: { amount: number; status?: string; payments?: { amount: number; status?: string }[] }): number {
  const pays = (inv.payments ?? []).filter(isConfirmed).reduce((s, x) => s + x.amount, 0);
  // an invoice marked paid before receipts were tracked still counts in full
  return pays > 0 ? Math.min(pays, inv.amount) : inv.status === "paid" ? inv.amount : 0;
}
/** Money the client says they have sent, still awaiting confirmation. */
export function claimedPaid(inv: { payments?: { amount: number; status?: string }[] }): number {
  return (inv.payments ?? []).filter((x) => !isConfirmed(x)).reduce((s, x) => s + x.amount, 0);
}
/* Tax is a pass-through: the invoice `amount` is gross, but only the NET counts
   toward the fee. `net`/`taxRate` are captured on the invoice when tax applied;
   without them the whole amount is net (no tax). A received payment settles the
   invoice gross, so it is split net:tax in the invoice's own ratio. */
type TaxInv = { amount: number; net?: number; taxRate?: number; status?: string; payments?: { amount: number; status?: string }[] };
export function netOf(inv: TaxInv): number { return inv.net ?? inv.amount; }
export function taxOf(inv: TaxInv): number { return Math.max(0, inv.amount - netOf(inv)); }
/** The NET portion of what has actually been received (excludes VAT). */
export function confirmedNet(inv: TaxInv): number {
  const paid = confirmedPaid(inv);
  return inv.amount > 0 ? Math.round(paid * (netOf(inv) / inv.amount)) : paid;
}
/** The TAX portion of what has actually been received. */
export function confirmedTax(inv: TaxInv): number {
  return confirmedPaid(inv) - confirmedNet(inv);
}

export function billingTotals(p: Project): BillingTotals | null {
  const b = p.billing;
  if (!b) return null;
  const received = (id?: string) => {
    const inv = p.invoices.find((i) => i.id === id);
    return inv ? confirmedPaid(inv) : 0;
  };

  /* Count the INVOICES themselves, not the things that generated them. A
     project can carry a mix — milestones invoiced before a valuation took
     over, valuations, monthly draws — and only the invoice ledger sees all
     of it. Anything else silently loses money that was genuinely billed.
     An invoice still sitting at "scheduled" has not been issued, so it is
     neither billed nor owed — it belongs with the work still to come. */
  const issued = p.invoices.filter((i) => i.status !== "scheduled");
  // NET of tax throughout — VAT is a pass-through and never counts toward how
  // much of the FEE has been billed or paid (it has its own figures below).
  const invoiced = issued.reduce((s, i) => s + netOf(i), 0);
  const paid = issued.reduce((s, i) => s + confirmedNet(i), 0);
  const billable = b.contractSum;
  const scheduled = Math.max(0, billable - invoiced);
  const allocated = b.milestones.reduce((s, m) => s + m.amount, 0);
  /* Due is per invoice, so an overpayment on one never cancels a debt on
     another. NET of tax to stay consistent with invoiced/paid above — the VAT
     still owed is carried in taxDue, and the client's total cash to pay is
     dueNow + taxDue. */
  const dueNow = issued.reduce((s, i) => s + Math.max(0, netOf(i) - confirmedNet(i)), 0);
  // Tax is a pass-through, tracked apart from the contract figures — it never
  // counts toward how much of the fee has been invoiced or paid.
  const taxInvoiced = issued.reduce((s, i) => s + taxOf(i), 0);
  const taxPaid = issued.reduce((s, i) => s + confirmedTax(i), 0);
  // gross balance the client is actually asked for (fee + unpaid VAT)
  const dueGross = issued.reduce((s, i) => s + Math.max(0, i.amount - confirmedPaid(i)), 0);
  return {
    billable, invoiced, paid, dueNow,
    remainingToBill: Math.max(0, billable - invoiced),
    notYetBilled: Math.max(0, billable - invoiced),
    deferred: b.billingMode === "valuation"
      // in valuation mode the deferral is real money already held back, not a projection
      ? (b.valuations ?? []).reduce((s, v) => s + valuationTotals(v).deferred, 0)
      : b.deferred ? Math.round(billable * b.deferred.pct) : 0,
    taxInvoiced, taxPaid, taxDue: Math.max(0, taxInvoiced - taxPaid), dueGross,
    scheduled, outstanding: dueNow,
    pctInvoiced: billable > 0 ? invoiced / billable : 0,
    pctPaid: billable > 0 ? paid / billable : 0,
    allocated, unallocated: billable - allocated,
  };
}

/* ------------------------------------------------------ stage valuations */

export interface ValuationRow {
  id: string; scope: string; value: number; pctComplete: number;
  subTotal: number; deferred: number; total: number;
}
export interface ValuationTotals {
  rows: ValuationRow[];
  value: number; subTotal: number; deferred: number; total: number;
}

/** Certify a stage line by line: earned = value × % complete, the deferral is
    held off the earned amount, and the net is what's payable now. */
export function valuationTotals(v: import("./types").StageValuation): ValuationTotals {
  const rows: ValuationRow[] = v.lines.map((l) => {
    const subTotal = Math.round(l.value * l.pctComplete);
    const deferred = Math.round(subTotal * v.deferredPct);
    return { id: l.id, scope: l.scope, value: l.value, pctComplete: l.pctComplete, subTotal, deferred, total: subTotal - deferred };
  });
  const sum = (k: keyof ValuationRow) => rows.reduce((s, r) => s + (r[k] as number), 0);
  return { rows, value: sum("value"), subTotal: sum("subTotal"), deferred: sum("deferred"), total: sum("total") };
}

/** Split a stage's value across disciplines in the engagement's own ratio. */
export function valuationFromDisciplines(
  stageValue: number,
  disciplines: { name: string; amount: number }[],
  deferredPct: number,
  stage = "Preconstruction",
): import("./types").StageValuation {
  const whole = disciplines.reduce((s, d) => s + d.amount, 0) || 1;
  return {
    id: uid("val-"), stage, deferredPct,
    lines: disciplines.map((d) => ({
      id: uid("vl-"), scope: d.name,
      value: Math.round(stageValue * (d.amount / whole)),
      pctComplete: 0,
    })),
  };
}

/** The next monthly draw on a stage: the balance still to bill, spread over
    the months still to run. Re-derived each time, so a month billed early or
    a changed balance simply re-spreads the remainder. */
export function monthlyDraw(v: import("./types").StageValuation) {
  const m = v.monthly;
  const value = v.lines.reduce((s, l) => s + l.value, 0);
  if (!m) return null;
  const remainingMonths = Math.max(0, m.months - m.drawn);
  const balance = Math.max(0, value - m.drawnAmount);
  const gross = remainingMonths > 0 ? Math.round(balance / remainingMonths) : balance;
  const deferred = Math.round(gross * v.deferredPct);
  return { value, balance, remainingMonths, monthsDrawn: m.drawn, months: m.months, gross, deferred, net: gross - deferred };
}

/* ---------------------------------------------------------- payment links */

/** A deep link straight to this milestone in the client's billing tab. */
export function paymentLinkUrl(p: Project, m: PaymentMilestone): string {
  const origin = typeof location !== "undefined" ? location.origin : "https://studiogy.design";
  return `${origin}/portal/${p.id}/billing?m=${m.id}`;
}

/** The message that goes with the link — what the payment covers, itemised. */
export function paymentLinkText(p: Project, m: PaymentMilestone, fmt: (n: number) => string): string {
  const lines = (m.items ?? []).map((i) => `• ${i.label}${i.amount ? ` — ${fmt(i.amount)}` : ""}`).join("\n");
  return (
    `${p.name} — ${m.label}\n` +
    `${fmt(m.amount)}${m.dueOn ? ` · due ${m.dueOn}` : ""}\n` +
    (m.basis ? `\n${m.basis}\n` : "") +
    (lines ? `\nWhat this covers:\n${lines}\n` : "") +
    `\nThe full breakdown and the invoice are here:\n${paymentLinkUrl(p, m)}`
  );
}

/** Where the client can read the account, with every invoice and receipt. */
export function standingLink(p: Project): string {
  return `https://studiogy.design/portal/${p.id}/billing`;
}
/** The account as it stands, in plain lines — what a WhatsApp or an email
    carries: contract value, invoiced, received, due now, each open invoice
    with its date, and the link. */
export function standingText(p: Project, fmt: (n: number) => string): string {
  const t = billingTotals(p);
  const today = new Date().toISOString().slice(0, 10);
  const open = (p.invoices ?? []).filter((i) => i.status === "due" || i.status === "overdue");
  const bal = (i: Project["invoices"][number]) => Math.max(0, i.amount - confirmedPaid(i));
  const owed = open.reduce((s, i) => s + bal(i), 0);
  const lines = open.map((i) => {
    const days = Math.round((Date.parse(i.dueOn) - Date.parse(today)) / 86_400_000);
    const when = days < 0 ? `overdue ${-days}d` : days === 0 ? "due today" : `due ${i.dueOn}`;
    return `• ${i.ref} — ${i.title} — ${fmt(bal(i))} (${when})`;
  });
  return (
    `${p.name} (${p.code}) — account standing as at ${today}\n` +
    (t ? `Contract value ${fmt(t.billable)} · invoiced ${fmt(t.invoiced)} · received ${fmt(t.paid)}\n` : "") +
    `Balance due now: ${fmt(owed)}\n` +
    (lines.length ? `\nOpen invoices:\n${lines.join("\n")}\n` : "\nNo invoices outstanding.\n") +
    (t && t.remainingToBill > 0 ? `\nStill to bill on the engagement: ${fmt(t.remainingToBill)}\n` : "") +
    `\nEvery invoice and receipt, and where to pay:\n${standingLink(p)}`
  );
}

/** Money received against an invoice, and what's left on it. */
export function invoiceReceived(p: Project, invoiceId?: string): { received: number; balance: number; claimed: number } | null {
  const inv = p.invoices.find((i) => i.id === invoiceId);
  if (!inv) return null;
  const received = confirmedPaid(inv);
  return { received, balance: Math.max(0, inv.amount - received), claimed: claimedPaid(inv) };
}

/** The next milestone that could be invoiced. */
export function nextToInvoice(p: Project): PaymentMilestone | undefined {
  return p.billing?.milestones.find((m) => m.status === "scheduled");
}
